import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a paginated results list.
 * Supports infinite scroll (via PaginatedList) and load-more button mode.
 */
export default class ResultsList extends PaginatedList {
  /** @type {boolean} */
  #isLoadMoreMode = false;

  /** @type {boolean} */
  #isLoadingMore = false;

  /** @type {number} */
  #currentPage = 1;

  /** @type {number} */
  #lastPage = 1;

  /** @type {PaginatedListAspectRatioHelper | undefined} */
  #loadMoreAspectRatioHelper;

  connectedCallback() {
    super.connectedCallback();

    this.#isLoadMoreMode = this.getAttribute('pagination-type') === 'load_more';

    if (this.#isLoadMoreMode) {
      this.#initLoadMore();
    }

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);

    if (this.#isLoadMoreMode) {
      const { loadMoreButton } = this.refs;
      if (loadMoreButton) {
        loadMoreButton.removeEventListener('click', this.#handleLoadMore);
      }
    }
  }

  /**
   * Initializes load-more mode: prefetches next page and binds button click.
   */
  #initLoadMore() {
    const { loadMoreButton, grid } = this.refs;
    if (!loadMoreButton) return;

    // Read initial page state from the DOM
    const lastCard = grid?.querySelector('[ref="cards[]"]:last-of-type');
    this.#currentPage = lastCard ? Number(lastCard.dataset.page) : 1;
    this.#lastPage = grid ? Number(grid.dataset.lastPage) : 1;

    loadMoreButton.addEventListener('click', this.#handleLoadMore);

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#loadMoreAspectRatioHelper = new PaginatedListAspectRatioHelper({ templateCard });
    }

    // Prefetch the next page for faster load
    this.#prefetchNextPage();
  }

  /**
   * Gets the next page info based on the internal page counter.
   * @returns {{ page: number, url: URL } | null}
   */
  #getNextPageInfo() {
    const nextPage = this.#currentPage + 1;

    if (nextPage > this.#lastPage) return null;

    const url = new URL(window.location.href);
    url.searchParams.set('page', nextPage.toString());
    url.hash = '';

    return { page: nextPage, url };
  }

  /**
   * Prefetches the next page HTML via section renderer.
   */
  async #prefetchNextPage() {
    const nextPageInfo = this.#getNextPageInfo();
    if (!nextPageInfo) return;

    // Only prefetch if not already cached in PaginatedList's pages map
    if (this.pages.has(nextPageInfo.page)) return;

    const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, nextPageInfo.url);
    this.pages.set(nextPageInfo.page, pageContent);
  }

  /**
   * Handles the load more button click.
   */
  #handleLoadMore = async () => {
    if (this.#isLoadingMore) return;

    const { grid, loadMoreButton, loadMoreContainer } = this.refs;
    if (!grid || !loadMoreButton) return;

    const nextPageInfo = this.#getNextPageInfo();
    if (!nextPageInfo) return;

    this.#isLoadingMore = true;
    loadMoreButton.disabled = true;

    // Fetch if not already cached
    if (!this.pages.has(nextPageInfo.page)) {
      const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, nextPageInfo.url);
      this.pages.set(nextPageInfo.page, pageContent);
    }

    const pageHTML = this.pages.get(nextPageInfo.page);
    if (!pageHTML) {
      this.#isLoadingMore = false;
      loadMoreButton.disabled = false;
      return;
    }

    // Parse and extract grid items from the fetched page
    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) {
      this.#isLoadingMore = false;
      loadMoreButton.disabled = false;
      return;
    }

    const newItems = gridElement.querySelectorAll(':scope > [ref="cards[]"]');
    if (newItems.length > 0) {
      grid.append(...newItems);
      this.#loadMoreAspectRatioHelper?.processNewElements();
      history.pushState('', '', nextPageInfo.url.toString());
      this.#currentPage = nextPageInfo.page;
    }

    this.#isLoadingMore = false;
    loadMoreButton.disabled = false;

    // Check if there are more pages; hide button if not
    const followingPageInfo = this.#getNextPageInfo();
    if (!followingPageInfo) {
      if (loadMoreContainer) {
        loadMoreContainer.style.display = 'none';
      } else {
        loadMoreButton.style.display = 'none';
      }
    } else {
      // Prefetch next page for faster subsequent loads
      requestIdleCallback(() => {
        this.#prefetchNextPage();
      });
    }
  };

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
